<template>
  <CustomLineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    class="make-a-copy"
    v-bind="$attrs"
    @reload="getData"
  >
    <v-row justify="center">
      <v-col
        cols="10"
        class="custom-line-chart-select"
      >
        <SelectStocks
          :stock="ativeStock"
          :item-id="itemId"
          :on-change="toggle"
        />
      </v-col>
    </v-row>
  </CustomLineChart>
</template>

<script>
import CustomLineChart from '@/components/cards/models/CustomLineChart';
import SelectStocks from '@/components/cards/SelectStocks';
import EventBus from '@/utils/event-bus';
import { mapActions } from 'vuex';
import sentimentModel from '@/mixins/sentiment-model';
import { POST_FIELDS, OPERATION } from '@/services/charts-service';
import colors from '@/utils/colors';
import { getMetrics } from '@/utils/stocks';
import { sortSeries } from '@/utils/sorting';

const {
  forunsColor,
} = colors;

export default {
  name: 'StockVsSentimentLineChart',
  components: {
    CustomLineChart,
    SelectStocks,
  },
  mixins: [
    sentimentModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    metricToCompare: {
      type: String,
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    stock: {
      type: Object,
      default: () => {
        return { name: 'VALE3', symbol: 'VALE3.SA' };
      },
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      ativeStock: this.stock,
      totalPosts: 0,
      pointsParser: 'series',
      filteredValues: [],
      categories: [],
      cardOptions: {
        plotOptions: {
          areaspline: {
            stacking: 'normal',
            lineWidth: 0.5,
            lineColor: 'black',
            marker: {
              enabled: false,
            },
          },
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value: %d. %b}',
          },
        },
        yAxis: [{
          title: {
            text: this.metricToCompare,
          },
        },
        {
          title: {
            text: '',
          },
          labels: {
            format: '{value}',
          },
          opposite: true,
        },
        ],
        tooltip: {
          xDateFormat: '%d/%m/%Y',
          pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
        },
      },
    };
  },
  computed: {
    undefinedLabeL() {
      return this.$t('DataManagement.undefined');
    },
    selectLabel() {
      return this.$t('stocks.bovespa');
    },
    seriesNames() {
      return [
        this.getSentimentLabel('Positivo'),
        this.getSentimentLabel('Negativo'),
        this.getSentimentLabel('Neutro'),
      ];
    },
    series() {
      if (!this.rawSeries && !this.rawSeries.length) {
        return [];
      }
      let series = this.rawSeries;

      if (this.filteredValues.length) {
        series = series?.filter(point => {
          return this.filteredValues.includes(point.name);
        }) || [];
      }

      const organizedSeries = series?.map(serie => {
        return {
          name: serie.name || this.undefinedLabeL,
          data: serie.data.map((val, index) => [this.categories[index], val || 0]),
          yAxis: serie.yAxis || 0,
          type: serie.type || 'spline',
          color: serie.color || this.getSentimentColor(serie.name),
          zIndex: serie.zIndex,
          marker: {
            enabled: false,
          },
        };
      });

      const sortedSeries = sortSeries(organizedSeries);
      return sortedSeries;
    },
  },
  watch: {
    '$i18n.locale'() {
      this.updateYAxisTitles();
      if (this.metricToCompare === 'price') {
        this.cardOptions.yAxis[0].title.text = this.$t('stocks.price');
      }
    },
  },
  created() {
    this.getData();
    this.updateYAxisTitles();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
      'getWarroomService',
      'getIbovespaService',
    ]),
    updateYAxisTitles() {
      this.cardOptions.yAxis[1].title.text = this.$t('components.StockVsSentimentLineChart.label.sentimentText');
    },
    orderDataArrayAsc(data) {
      return data.sort();
    },

    getSentimentData({ points, axi }, categories) {
      const sentimentData = [];
      const fullDaysSorted = axi.values.sort();
      const indexs = [];
      fullDaysSorted.map(day => {
        if (categories.includes(day)) {
          indexs.push(fullDaysSorted.indexOf(day));
        }
        return day;
      });
      points.map(({ name, data }) => {
        const newData = [];
        indexs.map(index => {
          newData.push(data[index]);
          return index;
        });
        sentimentData.push({
          name,
          data: newData,
        });
        return sentimentData;
      });

      return sentimentData;
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      Promise.all([
        this.getChartsService({
          itemId: this.itemId,
          method: 'getTimeSeries',
          params: {
            grouping: [POST_FIELDS.POLARITY],
            operators: this.operation,
          },
        }),
        this.getIbovespaService({
          servicePath: 'getStocksData',
          stockParams: {
            stockSymbol: this.ativeStock.symbol,
          },
        }),
      ]).then(async (values) => {
        const THREE_HOURS_MILLIS = 10800000;
        const [warroomResponse, ibovespaResponse] = values;
        this.requestStatus = 'success';

        if (options?.silentRefresh) {
          EventBus.$emit('updated', { itemId: this.itemId });
        }
        const { points, axes: [axi] } = this.normalizeSentimentResponseFromChartService(warroomResponse);
        const categories = axi.values.sort();
        categories.forEach((category, index) => {
          categories[index] = category - THREE_HOURS_MILLIS;
        });
        const metric = await getMetrics({ ibovespaResponse, categories, metricToCompare: this.metricToCompare });

        categories.forEach((category, index) => {
          categories[index] = category + THREE_HOURS_MILLIS;
        });

        const sentimentData = this.getSentimentData({ points, axi }, categories);
        const series = [
          {
            'yAxis': 0,
            'name': this.translateMetric(this.metricToCompare),
            'data': metric,
            'color': forunsColor,
            'zIndex': 2,

          },
        ];
        sentimentData.map(point => {
          series.push({
            'yAxis': 1,
            'name': point.name,
            'data': point.data,
            'type': 'areaspline',
            'zIndex': 1,
          });
          return series;
        });
        this.categories = this.orderDataArrayAsc([...categories]);
        this.rawSeries = series;
        if (this.metricToCompare === 'price') {
          this.cardOptions.yAxis[0].labels = {
            formatter() {
              return `R$${this.value.toLocaleString('pt-BR')}`;
            },
          };
          this.cardOptions.yAxis[0].title.text = this.$t('stocks.price');
        }
      })
        .catch(() => {
          this.requestStatus = 'error';
        });
    },
    toggle(ativeStock) {
      this.ativeStock = ativeStock;
      this.getData();
    },
    translateMetric(metric) {
      const dict = {
        volume: this.$t('stocks.volume'),
        price: this.$t('stocks.price'),
      };
      return metric in dict ? dict[metric] : metric;
    },
  },
};
</script>

<style scoped lang="scss">
  .custom-line-chart-select {
    margin: auto;
    padding: 0;
  }

  ::v-deep {
    .line-chart-container {
      height: calc(100% - 40px) !important;
    }

    .line-chart {
      height: calc(100% - 40px) !important;
    }
  }
</style>
